import { useState } from 'react';
import type {
  CoverageEligibilityRecord,
  EligibilityFieldProvenance,
} from '@features/patient-coverages/types';

import type { FinancialFieldDef } from '../utils/verificationFinancials';
import { seedFinancialInputs } from '../utils/verificationFinancials';

type BenefitInputField = FinancialFieldDef['field'];

/** UI inputs, deliberately not a request contract. The command adapter owns serialization. */
export interface ManualEligibilityDraft {
  result: NonNullable<CoverageEligibilityRecord['coverage_result']> | '';
  method: NonNullable<EligibilityFieldProvenance['method']>;
  contactedAt: string;
  representativeName: string;
  referenceNumber: string;
  notes: string;
  financials: Record<string, string>;
  assertedFields: ReadonlySet<BenefitInputField>;
}

export function localContactTime(date: Date): string {
  return new Date(date.getTime() - date.getTimezoneOffset() * 60_000).toISOString().slice(0, 16);
}

/** Mount one editing session per selected coverage; refetches never reseed its draft. */
export function useManualEligibilityDraft(initialRecord: CoverageEligibilityRecord) {
  const [draft, setDraft] = useState<ManualEligibilityDraft>(() => ({
    result: '',
    method: 'portal',
    contactedAt: localContactTime(new Date()),
    representativeName: '',
    referenceNumber: '',
    notes: '',
    financials: seedFinancialInputs(initialRecord.benefits ?? {}),
    assertedFields: new Set(),
  }));

  function changeBenefit(field: BenefitInputField, value: string): void {
    setDraft(previous => ({
      ...previous,
      financials: { ...previous.financials, [field]: value },
      assertedFields: new Set([...previous.assertedFields, field]),
    }));
  }

  return { draft, setDraft, changeBenefit };
}
